/**
 * MiniPlayer — floating bar that appears above the bottom nav when a ringtone is
 * loaded. Tapping it opens the full-screen player.
 *
 * Positioned just above the bottom nav pill (z-index 150, below nav's 200).
 * Uses MiniPlayerBar for the visual content so all players look identical.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRingtonePlayerState, useRingtonePlayerActions } from '@/contexts/RingtonePlayerContext';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';

interface MiniPlayerProps {
  /** When true, tapping the bar does NOT open the full-screen player */
  disableFullScreen?: boolean;
  /** Distance from the bottom of the viewport in px. Default 16 (sits at the bottom edge).
   *  Pages with a bottom nav pill should pass 80 so the player sits above the nav. */
  bottomOffsetPx?: number;
}

export function MiniPlayer({ disableFullScreen = false, bottomOffsetPx = 16 }: MiniPlayerProps) {
  const { currentRingtone, isPlaying, currentTime, duration, floatingPlayerEnabled, fullScreenOpen } = useRingtonePlayerState();
  const { pause, resume, playNext, openFullScreen, stop } = useRingtonePlayerActions();

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaying) pause();
    else resume();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    playNext();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    stop();
  };

  const handleBarClick = () => {
    if (!disableFullScreen) {
      openFullScreen();
    }
  };

  if (!currentRingtone || !floatingPlayerEnabled || fullScreenOpen) return null;

  const title = currentRingtone.title ?? 'Untitled';
  const category = (currentRingtone as { category?: string }).category ?? 'Ringtone';

  return (
    <AnimatePresence>
      {currentRingtone && (
        <>
          <motion.div
            key="mini-player"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 32 }}
            style={{
              position: 'fixed',
              left: '1rem',
              right: '1rem',
              willChange: 'transform, bottom',
              // Position relative to the bottom of the viewport. Defaults to 16px (bottom edge);
              // pages with a bottom nav pass 80 so it sits above the pill. When the nav
              // hides (modal open, scrolled away), callers lower this back to 16 and the
              // player slides down to fill the space — matched to the nav's 300ms ease.
              bottom: `calc(${bottomOffsetPx}px + env(safe-area-inset-bottom, 0px))`,
              transition: 'bottom 300ms ease',
              maxWidth: '28rem',
              margin: '0 auto',
              // Must be above all modals (SeeAllModal/SeeAllRingtonesModal at 9999)
              // and above the bottom nav (10001). Full-screen player is at 220 but
              // it calls floatingPlayerEnabled=false via stop() so mini-player
              // disappears while the full-screen player is open anyway.
              zIndex: 10050,
            }}
          >
            <MiniPlayerBar
              title={title}
              subtitle={category}
              isPlaying={isPlaying}
              progress={progress}
              onPlayPause={handlePlayPause}
              onNext={handleNext}
              onDismiss={handleClose}
              showNextButton={true}
              showDismissButton={true}
              onBarClick={disableFullScreen ? undefined : handleBarClick}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default MiniPlayer;
